'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { ThankYouMark } from '@/components/forms/ThankYouMark'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { omitContact, reportToLeadCentre, type LeadCentreLeadType } from '@/utilities/leadCentre'

/**
 * Guide titles carry no structured category field in the Guides collection — just
 * title/slug — so the Lead Centre lead_type is inferred from the title text.
 * Guides that clearly target one side of a transaction map to it; guides that
 * could serve either side (Property Management, Commercial, Auction) fall back to
 * OTHER rather than guessing (Lead Centre wiring brief, 2026-08-24 — flagged as an
 * ambiguous call).
 */
function leadCentreTypeForGuide(guideTitle: string): LeadCentreLeadType {
  const t = guideTitle.toLowerCase()
  if (t.includes('seller')) return 'SELLER_APPRAISAL'
  if (t.includes('renting') || t.includes('rental')) return 'RENTAL_ENQUIRY'
  if (t.includes('buyer') || t.includes('first home') || t.includes('investment')) {
    return 'BUYER_ENQUIRY'
  }
  return 'OTHER'
}

/**
 * Guide download gate — Figma "Guide Title" template: "Please confirm your
 * details." + first/last/email/mobile, "Download Guide" CTA. Submitting records
 * a guide-request lead, then opens the document.
 */
export function GuideDownloadForm({
  guideId,
  guideTitle,
  documentUrl,
}: {
  guideId: number | string
  guideTitle: string
  documentUrl: string | null
}) {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', mobile: '' })
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  const update = (patch: Partial<typeof form>) => {
    setForm((prev) => ({ ...prev, ...patch }))
    setError('')
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('Please enter your first and last name.')
      return
    }
    if (!/^\S[^\s@]*@\S+\.\S+$/.test(form.email.trim())) {
      setError('A valid email is required.')
      return
    }
    if (!/^[\d\s+()-]{8,15}$/.test(form.mobile.trim())) {
      setError('A valid mobile number is required.')
      return
    }
    setSubmitting(true)
    setError('')
    try {
      const res = await fetch('/api/guide-downloads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, guideId }),
      })
      if (!res.ok) throw new Error('failed')
      const json = await res.json()
      setDone(true)
      if (json.downloadUrl) {
        window.open(json.downloadUrl, '_blank', 'noopener')
      }

      reportToLeadCentre({
        name: [form.firstName, form.lastName].filter(Boolean).join(' '),
        email: form.email,
        phone: form.mobile,
        leadType: leadCentreTypeForGuide(guideTitle),
        sourceForm: `guide-download-${guideId}`,
        fields: {
          guide: guideTitle,
          ...omitContact(form, ['firstName', 'lastName', 'email', 'mobile']),
        },
      })
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <div className="flex flex-col items-start gap-6">
        {/* Asana 018 — shared thank-you mark on all form success states */}
        <ThankYouMark />
        {/* Asana 010 — thanks line styled like the Offer To Purchase thank-you
            heading (font-display display-2xl regular). */}
        <h2 className="font-display text-display-2xl font-normal text-foreground">
          Thanks — your download of the {guideTitle} has started.
        </h2>
        {documentUrl ? (
          <Button asChild variant="outline">
            <a href={documentUrl} target="_blank" rel="noopener noreferrer">
              Download again
            </a>
          </Button>
        ) : (
          <p className="max-w-xl text-body-md font-light text-foreground">
            We&rsquo;ll email you a copy shortly.
          </p>
        )}
      </div>
    )
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-8">
      <div className="flex flex-col gap-6">
        <p className="text-body-md font-normal text-foreground">Please confirm your details.</p>
        <div className="flex flex-col gap-2">
          <Label htmlFor="guide-firstName">First Name</Label>
          <Input
            id="guide-firstName"
            value={form.firstName}
            onChange={(e) => update({ firstName: e.target.value })}
            placeholder="e.g. Alex"
            autoComplete="given-name"
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="guide-lastName">Last Name</Label>
          <Input
            id="guide-lastName"
            value={form.lastName}
            onChange={(e) => update({ lastName: e.target.value })}
            placeholder="e.g. Smith"
            autoComplete="family-name"
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="guide-email">Email</Label>
          <Input
            id="guide-email"
            type="email"
            value={form.email}
            onChange={(e) => update({ email: e.target.value })}
            placeholder="your@email.com"
            autoComplete="email"
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="guide-mobile">Mobile</Label>
          <Input
            id="guide-mobile"
            type="tel"
            value={form.mobile}
            onChange={(e) => update({ mobile: e.target.value })}
            placeholder="04XX XXX XXX"
            autoComplete="tel"
          />
        </div>
      </div>
      {error && <p className="text-body-sm font-light text-destructive">{error}</p>}
      <div className="flex justify-start">
        <Button type="submit" loading={submitting}>
          Download Guide
        </Button>
      </div>
    </form>
  )
}
